/* eslint-disable no-param-reassign */
import _without from 'lodash/without'

import settings from 'app/settings'
import isAdmin from 'data/utils/isAdmin'

import { fetchWithAuth as actualFetchWithAuth } from './fetchWithAuth'
import handleErrorResponse from './handleErrorResponse'

/** @deprecated
 *  Use data/utils/fetchWithAuth instead
 * */
export function fetchWithAuth(url, params, options = {}) {
    return actualFetchWithAuth(url, params, options)
}

export const acceptHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
}

export const fetchAndReturn = (url, params, process, onError, options) => {
    if (!process) process = (string) => string.json()
    return actualFetchWithAuth(url, params, options)
        .then((response) => {
            if (response.status >= 400) {
                handleErrorResponse(response.clone())
                return Promise.reject(response.clone())
            }
            let result = {}

            try {
                result = response.json()
            } catch (e) {
                // ?
            }
            return Promise.resolve(result)
        })
        .catch((error) => {
            if (onError) onError(error, { url: url, params: params })
            return Promise.reject(error)
        })
}

export const fetchAndReturnRaw = (url, params, process, options) => {
    if (!process) process = (string) => string.json()
    return actualFetchWithAuth(url, params, options).then((response) => {
        if (response.status >= 400) {
            return Promise.reject(response)
        }
        return response
    })
}

export class StackerAPI {
    preprocessFromLocal = (data) => {
        let out = data
        if (this.transformFromLocal) {
            out = this.transformFromLocal(data)
        }
        return JSON.stringify(out)
    }

    preprocessToLocal = (data) => {
        let received = data
        if (this.transformToLocal) {
            received = Array.isArray(received)
                ? received.map(this.transformToLocal)
                : this.transformToLocal(received)
        }
        return received
    }

    onFetchFailed = () => {}

    path = ''

    get(id) {
        const path = this.path + (id ? `${id}/` : ``)
        return fetchAndReturn(path, {}, this.preprocessToLocal, this.onFetchFailed, {
            bypassPreviewAndImpersonation: this.bypassPreviewAndImpersonation,
        })
    }

    post(data, options) {
        return fetchAndReturn(
            this.path,
            {
                method: 'POST',
                headers: acceptHeaders,
                body: this.preprocessFromLocal(data),
            },
            null,
            null,
            {
                bypassPreviewAndImpersonation: this.bypassPreviewAndImpersonation,
                ...options,
            }
        )
    }

    delete(id, queryDict = null, options) {
        return fetchAndReturnRaw(
            buildUrl(`${this.path + id}/`, queryDict),
            {
                method: 'DELETE',
            },
            null,
            {
                bypassPreviewAndImpersonation: this.bypassPreviewAndImpersonation,
                ...options,
            }
        )
    }

    patch(id, data, options) {
        return fetchAndReturn(
            `${this.path + id}/`,
            {
                method: 'PATCH',
                headers: acceptHeaders,
                body: this.preprocessFromLocal(data),
            },
            null,
            null,
            {
                bypassPreviewAndImpersonation: this.bypassPreviewAndImpersonation,
                ...options,
            }
        )
    }
}

export const without = _without

export function buildUrl(url, parameters) {
    let qs = ''
    for (const key in parameters) {
        // eslint-disable-next-line no-prototype-builtins
        if (parameters.hasOwnProperty(key)) {
            const value = parameters[key]
            if (Array.isArray(value)) {
                value.forEach((v) => {
                    qs += `${v}&`
                })
            } else {
                qs += `${encodeURIComponent(key)}=${encodeURIComponent(value)}&`
            }
        }
    }
    if (qs.length > 0) {
        qs = qs.substring(0, qs.length - 1) // chop off last "&"
        url = url.indexOf('?') > -1 ? `${url}&${qs}` : `${url}?${qs}`
    }

    return url
}

// Methods for formatting different field types for display.

export const formatNumber = (value, mindp = 0, maxdp = 0) => {
    if (!value || isNaN(value)) return value
    return value.toLocaleString('en', {
        minimumFractionDigits: mindp,
        maximumFractionDigits: maxdp,
    })
}

export const formatValue = (field, value) => {
    let symbol
    let currencyValue
    switch (field.type) {
        case 'currency':
            symbol = field.options?.currency_symbol || ''
            value = parseFloat(value)
            if (value === null || value === undefined || Number.isNaN(value)) {
                return null
            }
            return symbol
                ? `${symbol}\u200A${formatNumber(value, 2, 2)}`
                : formatNumber(value, 2, 2)
        case 'percentage':
            value = parseFloat(value)
            if (value === null || value === undefined || Number.isNaN(value)) {
                return null
            }
            return `${formatNumber(value * 100.0, 0, 3)}\u200A%`
        case 'currency_varying':
            if (value === null || value === undefined) {
                return null
            }

            currencyValue = parseFloat(value.value)
            symbol = value.currency_symbol
            if (
                currencyValue === null ||
                currencyValue === undefined ||
                Number.isNaN(currencyValue)
            ) {
                return null
            }
            if (symbol) {
                return `${symbol}\u200A${formatNumber(currencyValue, 2, 2)}`
            } else {
                return formatNumber(currencyValue, 2, 2)
            }
        default:
            return value
    }
}

export const getStackForCurrentUrl = (workspaceAccountId, stacks) => {
    let host = settings.HOST

    let match
    // If we are in workspaces mode find the first stack under
    // the workspace account whose stack.url_slug matches the first
    // portion of our current page (ie., workspace.stacker.dev/{appname}/page)
    if (workspaceAccountId) {
        const prefix = window.location.pathname.split('/')[1]

        // no stack selected yet, we choose the first alphabetically
        if (!prefix) {
            match = orderAlphabeticallyStacks(stacks, workspaceAccountId)[0]
        } else {
            match = stacks.find(
                (stack) => stack.account_id === workspaceAccountId && stack.url_slug === prefix
            )
        }
    } else {
        match = stacks.find((stack) => stack.base_url === host || stack.custom_base_url === host)

        // if we're in v3 and the matched stack is a v4 app,
        // and we're logged in as a Stacker user, not an end user,
        // hard redirect to its equivalent workspace url
        //
        // i.e. don't allow v4 apps outside of their workspace, on a v3 url
        if (!!match?.options?.workspace_app && isAdmin()) {
            fetchWithAuth('accounts')
                .then((res) => {
                    return res.status === 200 ? res.json() : undefined
                })
                .then((json) => {
                    // this should always be present, and be an array of at least one account
                    //
                    // If it fails for some reason, just fail silently and render the v3 version
                    if (json?.length) {
                        // find the account for the selected app
                        const workspaceAccountForApp = json.find((account) => {
                            return account._sid === match.account_id
                        })

                        // redirect to the same path in the same app, but at the correct workspace URL
                        // which we get from the workspace account's base_url and the app's url_slug
                        if (workspaceAccountForApp) {
                            const workspaceAppBaseUrl = `//${workspaceAccountForApp.base_url}/${match.url_slug}`

                            window.location.href = workspaceAppBaseUrl + window.location.pathname
                        }
                    }
                })
        }
    }

    return match
}

export function orderAlphabeticallyStacks(stacks, accountId) {
    return stacks
        .filter((x) => x.account_id === accountId && x.options?.workspace_app)
        .sort((a, b) => {
            // put demo apps at the bottom, in alphabetical order
            if (a?.options?.is_demo && !b?.options?.is_demo) {
                return 1
            } else if (!a?.options?.is_demo && b?.options?.is_demo) {
                return -1
            }

            return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1
        })
}

// We need the keep the arguments for correct TS definition. To be removed once this file has been converted to TS.
// eslint-disable-next-line unused-imports/no-unused-vars
export function recordDeletionAvailable(object, stack) {
    if (!object) return false
    const rowProvider = object?.connected_tables_row_provider
    const readOnly = object?.connection_options.read_only

    if (rowProvider || readOnly) {
        return false
    }

    // Only Stacker Data, Airtable, and PostgresV2 for now
    if (object.connection_options.stacker_native_object) {
        return !!object.connection_options.postcache_has_deleted_column
    }

    if (
        object.connection_options.airtable_table ||
        // detect postgresV2/fivetran connections - fivetran is always read-only, so will get ignored elsewhere
        object.connection_options.simpleconn_metadata?.kind == 'table'
    ) {
        return true
    }

    return false
}
